import { request, API_BASE, resetLoginRedirectFlag, markLogoutInProgress } from "./utils/api";
import {
  persistCurrentUserSession,
  clearAllTenantSessionData,
} from "./utils/tenantContext";
import { normalizeEmail } from "./utils/email";
import { markPromoEligibleForLogin } from "./components/Promo/promoCampaignStorage";

export const registerUser = async (user) => {
  try {
    const payload = {
      ...user,
      email: normalizeEmail(user?.email),
    };
    const data = await request("/auth/register", {
      method: "POST",
      body: JSON.stringify(payload),
      skipAuth: true,
    });
    return { success: true, user: data.user };
  } catch (error) {
    return { success: false, message: error.message };
  }
};

export const loginUser = async ({ email, password }) => {
  try {
    const data = await request("/login", {
      method: "POST",
      body: JSON.stringify({ email: normalizeEmail(email), password }),
      skipAuth: true,
    });

    // Handle response structure where data might be wrapped in 'result'
    const payload = data.result || data;
    const user = payload.user || payload;
    const token = payload.token || user.token; // Extract token
    const modules = payload.modules || [];

    if (!token) {
      return {
        success: false,
        message: "Login succeeded but no session token was returned.",
      };
    }

    // Clear prior tenant caches before establishing a new session (shared workstation safety).
    clearAllTenantSessionData();

    // Store user with token and full session data in localStorage.
    persistCurrentUserSession({
      ...user, // includes user.role as { id, name, ... }
      token,
      tenantId: payload.tenantId ?? user.tenantId, // top-level tenantId
      tenant: payload.tenant ?? null, // full tenant object
      modules, // modules array with permissions
    });

    markPromoEligibleForLogin();
    resetLoginRedirectFlag();
    return { success: true, user, modules };
  } catch (error) {
    // Preserve backend copy (e.g. "Invalid email or password. Please try again.")
    return {
      success: false,
      message: error?.message || "Login failed. Please try again.",
    };
  }
};

// Updated to support query params object
export const getUsers = async (params = {}) => {
  try {
    // If params is boolean (legacy support), convert to object
    if (typeof params === 'boolean') {
      params = { excludeFiles: params };
    }

    // Convert object to query string
    const query = new URLSearchParams(params).toString();
    const url = query ? `/users?${query}` : "/users";

    return await request(url);
  } catch (error) {
    console.error("Failed to load users:", error.message);
    // Return empty result structure if failed
    return { data: [], total: 0, page: 1, limit: 10 };
  }
};

export const getRoles = async () => {
  try {
    const response = await request("/roles");
    return response.result?.roles || [];
  } catch (error) {
    console.error("Failed to load roles:", error.message);
    return [];
  }
};

export const getUserById = async (id) => {
  try {
    return await request(`/users/${id}`);
  } catch (error) {
    console.error("Failed to load user:", error.message);
    return null;
  }
};

export const addEmployee = async (user) => {
  const payload = {
    ...user,
    email: normalizeEmail(user?.email),
  };
  return request("/users", {
    method: "POST",
    body: JSON.stringify(payload),
  });
};

export const updateUser = async (id, payload) => {
  const body = { ...payload };
  if (Object.prototype.hasOwnProperty.call(body, "email") && body.email != null) {
    body.email = normalizeEmail(body.email);
  }
  return request(`/users/${id}`, {
    method: "PUT",
    body: JSON.stringify(body),
  });
};

export const updateUserStatus = async (id, status) => {
  const normalized =
    String(status || "").charAt(0).toUpperCase() +
    String(status || "").slice(1).toLowerCase();
  return request(`/users/${id}/status`, {
    method: "PATCH",
    body: JSON.stringify({ status: normalized }),
  });
};

export const deleteUser = async (id) => {
  return request(`/users/${id}`, {
    method: "DELETE",
  });
};

export const exportUsersCSV = async (params = {}) => {
  const query = new URLSearchParams(params).toString();
  const token = JSON.parse(localStorage.getItem("currentUser") || "{}").token;

  // For download, we might need to handle it differently since request returns JSON usually.
  // But request wrapper likely handles JSON. CSV endpoint returns text/csv?
  // If api.js request expects JSON, this might fail.
  // Usually export is done via direct window.open or fetch returning blob.
  // Let's keep the existing logic in Employees.js for now or move it to a helper that uses fetch directly.
  // However, the user asked to "access ... user.ts ... implement all api".
  // The router has `/users/export/csv`.

  const headers = {
    "Authorization": `${token}`
  };

  const response = await fetch(`${API_BASE}/users/export/csv?${query}`, { headers });
  if (!response.ok) throw new Error("Export failed");
  return response.blob();
};

export const updateUserCredential = async (id, credential, data) => {
  return request(`/users/${id}/credentials`, {
    method: "PATCH",
    body: JSON.stringify({ credential, data }),
  });
};

// Document API functions (new GridFS-based system)
export const uploadDocument = async (userId, credentialName, fileData) => {
  return request(
    `/users/${userId}/credentials/${encodeURIComponent(credentialName)}/documents`,
    {
      method: "POST",
      body: JSON.stringify({
        name: fileData.name,
        data: fileData.data,
        type: fileData.type,
        size: fileData.size,
      }),
    },
  );
};

export const getDocuments = async (userId, credentialName) => {
  try {
    // Route is /api/user/:userId/credential/:credentialName (not /api/documents/user/...)
    const response = await request(`/user/${userId}/credential/${encodeURIComponent(credentialName)}`);
    return response.documents || [];
  } catch (error) {
    console.error("Failed to get documents:", error.message);
    return [];
  }
};

export const deleteDocument = async (documentId) => {
  try {
    const response = await request(`/documents/${documentId}`, {
      method: "DELETE",
    });
    return response;
  } catch (error) {
    console.error("Failed to delete document:", error.message);
    throw error;
  }
};

export const getDocumentUrl = (fileId) => {
  const API_BASE = process.env.REACT_APP_API_URL?.replace(/\/$/, "") || "http://localhost:8081/api";
  // Use the simpler /api/files/:id route for direct GridFS access
  return `${API_BASE}/files/${fileId}`;
};

export const logoutUser = () => {
  markLogoutInProgress();
  clearAllTenantSessionData();
};
